import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { NoteCreateInput, NoteResponse, NotesResponse, NoteUpdateInput } from '@/models/note.ts';

// Get notes for a specific entity (lead or customer)
const fetchNotes = async (
    entityType: string,
    entityId: string,
    params?: {
        type?: string;
        activity_type?: string;
        assignee_id?: string;
        status?: string;
        priority?: string;
    }
) => {
    const { data } = await httpClient.get<NotesResponse>(
        API_ENDPOINTS.CRM.NOTES.LIST(entityType, entityId),
        { params }
    );
    return data;
};

export const useNotes = (
    entityType: string,
    entityId: string,
    params?: {
        type?: string;
        activity_type?: string;
        assignee_id?: string;
        status?: string;
        priority?: string;
        page?: number;
        per_page?: number;
    }
) => {
    return useQuery<NotesResponse, Error>({
        queryKey: ['notes', entityType, entityId, params],
        queryFn: () => fetchNotes(entityType, entityId, params),
    });
};

// Get a specific note
const fetchNote = async (entityType: string, entityId: string, noteId: string) => {
    const { data } = await httpClient.get<NoteResponse>(
        API_ENDPOINTS.CRM.NOTES.DETAILS(entityType, entityId, noteId)
    );
    return data;
};

export const useNote = (entityType: string, entityId: string, noteId: string) => {
    return useQuery<NoteResponse, Error>({
        queryKey: ['notes', entityType, entityId, noteId],
        queryFn: () => fetchNote(entityType, entityId, noteId),
        enabled: !!entityType && !!entityId && !!noteId,
    });
};

// Create a new note
const createNote = async (entityType: string, entityId: string, input: NoteCreateInput) => {
    const { data } = await httpClient.post<NoteResponse>(
        API_ENDPOINTS.CRM.NOTES.CREATE(entityType, entityId),
        input
    );
    return data;
};

export const useCreateNote = () => {
    const queryClient = useQueryClient();

    return useMutation<
        NoteResponse,
        Error,
        { entityType: string; entityId: string; input: NoteCreateInput }
    >({
        mutationFn: ({ entityType, entityId, input }) => createNote(entityType, entityId, input),
        onSuccess: (_, { entityType, entityId }) => {
            // Invalidate notes queries
            queryClient.invalidateQueries({
                queryKey: ['notes', entityType, entityId],
                exact: false,
            });

            // If this is a customer note, also invalidate customer queries
            if (entityType === 'customers') {
                queryClient.invalidateQueries({
                    queryKey: ['crm-customers', entityId],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['customers'],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['crm-customers'],
                    exact: false,
                });
            }

            // If this is a lead note, also invalidate lead queries
            if (entityType === 'leads') {
                queryClient.invalidateQueries({
                    queryKey: ['leads', entityId],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['leads'],
                    exact: false,
                });
            }
        },
    });
};

// Update a note
const updateNote = async (
    entityType: string,
    entityId: string,
    noteId: string,
    input: NoteUpdateInput
) => {
    const { data } = await httpClient.put<NoteResponse>(
        API_ENDPOINTS.CRM.NOTES.UPDATE(entityType, entityId, noteId),
        input
    );
    return data;
};

export const useUpdateNote = () => {
    const queryClient = useQueryClient();

    return useMutation<
        NoteResponse,
        Error,
        {
            entityType: string;
            entityId: string;
            noteId: string;
            input: NoteUpdateInput;
        }
    >({
        mutationFn: ({ entityType, entityId, noteId, input }) =>
            updateNote(entityType, entityId, noteId, input),
        onSuccess: (_, { entityType, entityId, noteId }) => {
            // Invalidate notes queries
            queryClient.invalidateQueries({
                queryKey: ['notes', entityType, entityId],
                exact: false,
            });
            queryClient.invalidateQueries({ queryKey: ['notes', entityType, entityId, noteId] });

            // If this is a customer note, also invalidate customer queries
            if (entityType === 'customers') {
                queryClient.invalidateQueries({
                    queryKey: ['crm-customers', entityId],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['customers'],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['crm-customers'],
                    exact: false,
                });
            }

            // If this is a lead note, also invalidate lead queries
            if (entityType === 'leads') {
                queryClient.invalidateQueries({
                    queryKey: ['leads', entityId],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['leads'],
                    exact: false,
                });
            }
        },
    });
};

// Delete a note
const deleteNote = async (entityType: string, entityId: string, noteId: string) => {
    await httpClient.delete<ApiResponse<void>>(
        API_ENDPOINTS.CRM.NOTES.DELETE(entityType, entityId, noteId)
    );
};

export const useDeleteNote = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { entityType: string; entityId: string; noteId: string }>({
        mutationFn: ({ entityType, entityId, noteId }) => deleteNote(entityType, entityId, noteId),
        onSuccess: (_, { entityType, entityId, noteId }) => {
            // Invalidate notes queries
            queryClient.invalidateQueries({
                queryKey: ['notes', entityType, entityId],
                exact: false,
            });
            queryClient.removeQueries({ queryKey: ['notes', entityType, entityId, noteId] });

            // If this is a customer note, also invalidate customer queries
            if (entityType === 'customers') {
                queryClient.invalidateQueries({
                    queryKey: ['crm-customers', entityId],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['customers'],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['crm-customers'],
                    exact: false,
                });
            }

            // If this is a lead note, also invalidate lead queries
            if (entityType === 'leads') {
                queryClient.invalidateQueries({
                    queryKey: ['leads', entityId],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: ['leads'],
                    exact: false,
                });
            }
        },
    });
};

// Get notes dashboard
const fetchNotesDashboard = async () => {
    const { data } = await httpClient.get<NotesResponse>(API_ENDPOINTS.CRM.NOTES.DASHBOARD);
    return data;
};

export const useNotesDashboard = () => {
    return useQuery<NotesResponse, Error>({
        queryKey: ['notes', 'dashboard'],
        queryFn: fetchNotesDashboard,
    });
};

// Convenience hooks for specific note types
export const useNotesByType = (
    entityType: string,
    entityId: string,
    type: 'note' | 'task' | 'activity'
) => {
    return useNotes(entityType, entityId, { type });
};

export const useTasks = (entityType: string, entityId: string) => {
    return useNotesByType(entityType, entityId, 'task');
};

export const useActivities = (entityType: string, entityId: string) => {
    return useNotesByType(entityType, entityId, 'activity');
};

export const useGeneralNotes = (entityType: string, entityId: string) => {
    return useNotesByType(entityType, entityId, 'note');
};
